import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Search, Phone, GraduationCap, UserCheck, Users, CalendarCheck,
  RefreshCw, Loader2, ChevronRight, Plus,
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';
import LeadDetailDrawer from '@/components/training/LeadDetailDrawer';
import { STAGES, usePipelineMove, MoveToSelect } from '@/components/training/PipelineMove';
import { formatGBP } from '@/components/training/KanbanBoard';

const StudentsPage = () => {
  const { user, selectedBusiness } = useAuth();
  const canEdit = ['admin', 'super_admin'].includes(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('leads');
  const [pipeline, setPipeline] = useState(null);
  const [allLeads, setAllLeads] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Add student dialog
  const [addDialog, setAddDialog] = useState(false);
  const [addingStudent, setAddingStudent] = useState(false);
  const [studentType, setStudentType] = useState('new_registration'); // 'new_registration' or 'already_registered'
  const emptyStudentForm = { name: '', phone: '', email: '', notes: '', course_id: '', registration_date: '', registration_time: '', lead_source_type: '', referred_by_lead_id: '' };
  const [studentForm, setStudentForm] = useState(emptyStudentForm);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerLeadId, setDrawerLeadId] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pipeRes, leadsRes, coursesRes] = await Promise.all([
        fetch(`${API_URL}/api/training/pipeline?period=all`, { credentials: 'include' }),
        fetch(`${API_URL}/api/leads?limit=500`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
        fetch(`${API_URL}/api/training/courses`, {
          headers: { 'Authorization': `Bearer ${token}` },
          credentials: 'include',
        }),
      ]);

      if (pipeRes.ok) setPipeline(await pipeRes.json());
      if (leadsRes.ok) setAllLeads(await leadsRes.json());
      if (coursesRes.ok) setCourses(await coursesRes.json());
    } catch (err) {
      console.error('Students fetch error:', err);
      toast.error('Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [token, selectedBusiness?.business_id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-open Add Student dialog when navigated with ?action=add-student
  useEffect(() => {
    if (searchParams.get('action') === 'add-student') {
      setAddDialog(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const openDrawer = (leadId) => {
    setDrawerLeadId(leadId);
    setDrawerOpen(true);
  };

  const { requestMove, moveDialog } = usePipelineMove(() => fetchData());

  const handleAddStudent = async () => {
    if (!studentForm.name.trim()) { toast.error('Please enter a name'); return; }
    if (studentForm.lead_source_type === 'referral' && !studentForm.referred_by_lead_id) {
      toast.error('Please choose who referred this student');
      return;
    }
    if (studentType === 'new_registration' && (!studentForm.registration_date || !studentForm.registration_time)) {
      toast.error('Please enter registration date and time');
      return;
    }
    setAddingStudent(true);
    try {
      const payload = {
        name: studentForm.name,
        phone: studentForm.phone,
        email: studentForm.email,
        notes: studentForm.notes,
        course_id: studentForm.course_id || undefined,
        student_type: studentType,
        lead_source_type: studentForm.lead_source_type || 'own',
      };
      if (studentForm.lead_source_type === 'referral') {
        payload.referred_by_lead_id = studentForm.referred_by_lead_id;
      }
      // Only include date/time for new registrations
      if (studentType === 'new_registration') {
        payload.registration_date = studentForm.registration_date;
        payload.registration_time = studentForm.registration_time;
      }

      const res = await fetch(`${API_URL}/api/training/students`, {
        method: 'POST',
        headers: authHeaders,
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();

        // Auto-enroll into course if selected
        if (studentForm.course_id && studentForm.course_id !== 'none' && data.lead_id) {
          try {
            const enrollRes = await fetch(`${API_URL}/api/training/courses/${studentForm.course_id}/enroll`, {
              method: 'POST',
              headers: authHeaders,
              credentials: 'include',
              body: JSON.stringify({ lead_id: data.lead_id }),
            });
            if (enrollRes.ok) {
              const course = courses.find(c => c.course_id === studentForm.course_id);
              if (studentType === 'new_registration') {
                const calMsg = data.ghl_calendar_booked ? ' and booked on the calendar' : '';
                toast.success(`${studentForm.name} registered, enrolled in ${course?.name || 'course'}${calMsg}`);
              } else {
                toast.success(`${studentForm.name} added and enrolled in ${course?.name || 'course'}`);
              }
            } else {
              const err = await enrollRes.json().catch(() => ({}));
              toast.success(`${studentForm.name} added but enrollment failed: ${err.detail || 'unknown error'}`);
            }
          } catch {
            toast.success(`${studentForm.name} added but enrollment failed`);
          }
        } else {
          if (studentType === 'new_registration') {
            const calMsg = data.ghl_calendar_booked ? ' and booked on the calendar' : '';
            toast.success(`${studentForm.name} registered${calMsg}`);
          } else {
            toast.success(`${studentForm.name} added successfully`);
          }
        }

        setAddDialog(false);
        setStudentForm(emptyStudentForm);
        setStudentType('new_registration');
        fetchData();
      } else {
        const data = await res.json();
        toast.error(data.detail || 'Failed to add student');
      }
    } catch (err) {
      toast.error('Failed to add student');
    } finally {
      setAddingStudent(false);
    }
  };

  const columns = pipeline?.columns || {};
  const stageStats = pipeline?.stages || {};
  const q = search.trim().toLowerCase();
  const filtered = (key) => (columns[key] || []).filter(c => !q
    || (c.name || '').toLowerCase().includes(q)
    || (c.phone || '').toLowerCase().includes(q)
    || (c.email || '').toLowerCase().includes(q)
    || (c.course || '').toLowerCase().includes(q));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 text-pink-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Students
          </h1>
          <p className="text-slate-500 mt-1">View and manage all your students</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={() => { setLoading(true); fetchData(); }}
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Button
            className="btn-primary rounded-xl px-4 h-9"
            onClick={() => setAddDialog(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Student
          </Button>
        </div>
      </div>

      {/* Stage summary (same stages + numbers as the Pipeline) */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {STAGES.map(st => (
          <button
            key={st.key}
            type="button"
            onClick={() => setActiveTab(st.key)}
            className={`rounded-2xl bg-white p-3 text-left shadow-sm border transition-all ${activeTab === st.key ? 'border-pink-300 ring-2 ring-pink-100' : 'border-transparent hover:border-slate-200'}`}
          >
            <p className="text-xs text-slate-500">{st.title}</p>
            <p className={`text-2xl font-bold ${STAGE_TEXT[st.key]}`}>{stageStats[st.key]?.count ?? 0}</p>
            {st.key !== 'drop_offs' && stageStats[st.key]?.value ? <p className="text-[11px] text-slate-400">{formatGBP(stageStats[st.key].value)} {['enrolled', 'completed'].includes(st.key) ? 'sold' : 'potential'}</p> : null}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, phone, email or course..."
          className="pl-10 rounded-xl h-11"
        />
      </div>

      {/* Stage tabs: Leads → Registered → Enrolled → Completed → Dropped out */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-1 px-1">
          <TabsList className="grid grid-cols-5 w-full min-w-[520px]">
            {STAGES.map(st => (
              <TabsTrigger key={st.key} value={st.key} className="text-xs sm:text-sm">
                {st.title} ({filtered(st.key).length})
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {STAGES.map(st => (
          <TabsContent key={st.key} value={st.key} className="mt-4">
            {filtered(st.key).length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 font-medium">{search ? 'No matches' : `No one in ${st.title}`}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {filtered(st.key).map(card => (
                  <StudentRow key={card.lead_id} card={card} stage={st.key} canEdit={canEdit}
                    onOpen={openDrawer} onMove={requestMove} />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Add Student Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Student</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Registration Type Toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setStudentType('new_registration')}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  studentType === 'new_registration'
                    ? 'border-pink-400 bg-pink-50 text-pink-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <CalendarCheck className="w-5 h-5 mx-auto mb-1" />
                <p className="text-sm font-medium">New Registration</p>
                <p className="text-xs text-slate-500 mt-0.5">Books a registration slot on the calendar</p>
              </button>
              <button
                type="button"
                onClick={() => setStudentType('already_registered')}
                className={`p-3 rounded-xl border-2 text-center transition-all ${
                  studentType === 'already_registered'
                    ? 'border-pink-400 bg-pink-50 text-pink-700'
                    : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                }`}
              >
                <UserCheck className="w-5 h-5 mx-auto mb-1" />
                <p className="text-sm font-medium">Already Registered</p>
                <p className="text-xs text-slate-500 mt-0.5">Already signed up, just add to the CRM</p>
              </button>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Name *</label>
              <Input
                value={studentForm.name}
                onChange={(e) => setStudentForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="rounded-xl h-11"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Phone</label>
              <Input
                value={studentForm.phone}
                onChange={(e) => setStudentForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="07700 900000"
                className="rounded-xl h-11"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
              <Input
                type="email"
                value={studentForm.email}
                onChange={(e) => setStudentForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="student@example.com"
                className="rounded-xl h-11"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Notes</label>
              <Input
                value={studentForm.notes}
                onChange={(e) => setStudentForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any extra info..."
                className="rounded-xl h-11"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Where did this student come from?</label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'own', label: 'Walk-in / own' },
                  { value: 'platform', label: 'Ads' },
                  { value: 'referral', label: 'Referred by a student' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStudentForm(prev => {
                      const next = prev.lead_source_type === opt.value ? '' : opt.value;
                      return { ...prev, lead_source_type: next, referred_by_lead_id: next === 'referral' ? prev.referred_by_lead_id : '' };
                    })}
                    className={`px-2 py-2 rounded-xl border-2 text-xs font-medium transition-all ${
                      studentForm.lead_source_type === opt.value
                        ? 'border-pink-400 bg-pink-50 text-pink-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            {studentForm.lead_source_type === 'referral' && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">Referred by *</label>
                <Select
                  value={studentForm.referred_by_lead_id}
                  onValueChange={(val) => setStudentForm(prev => ({ ...prev, referred_by_lead_id: val }))}
                >
                  <SelectTrigger className="rounded-xl h-11">
                    <SelectValue placeholder="Choose the student who referred them" />
                  </SelectTrigger>
                  <SelectContent>
                    {allLeads
                      .filter(l => l.lead_id && l.name)
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map(l => (
                        <SelectItem key={l.lead_id} value={l.lead_id}>{l.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Assign to Course</label>
              <Select
                value={studentForm.course_id}
                onValueChange={(val) => setStudentForm(prev => ({ ...prev, course_id: val }))}
              >
                <SelectTrigger className="rounded-xl h-11">
                  <SelectValue placeholder="No course (add later)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No course (add later)</SelectItem>
                  {courses
                    .filter(c => c.status !== 'completed')
                    .map(c => (
                      <SelectItem key={c.course_id} value={c.course_id}>
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-3 h-3 text-pink-500" />
                          {c.name}
                          {c.enrolled_count !== undefined && c.capacity && (
                            <span className="text-xs text-slate-400">({c.enrolled_count}/{c.capacity})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            {/* Only show date/time for new registrations */}
            {studentType === 'new_registration' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Registration Date *</label>
                  <Input
                    type="date"
                    value={studentForm.registration_date}
                    onChange={(e) => setStudentForm(prev => ({ ...prev, registration_date: e.target.value }))}
                    className="rounded-xl h-11"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Registration Time *</label>
                  <Input
                    type="time"
                    value={studentForm.registration_time}
                    onChange={(e) => setStudentForm(prev => ({ ...prev, registration_time: e.target.value }))}
                    className="rounded-xl h-11"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)} className="rounded-xl">Cancel</Button>
            <Button
              className="btn-primary rounded-xl"
              onClick={handleAddStudent}
              disabled={addingStudent}
            >
              {addingStudent ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {studentType === 'new_registration' ? 'Register Student' : 'Add Student'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {moveDialog}

      {/* Lead Detail Drawer */}
      <LeadDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        leadId={drawerLeadId}
        onLeadUpdated={fetchData}
      />
    </div>
  );
};


const STAGE_TEXT = {
  leads: 'text-slate-700', registrations: 'text-blue-600', enrolled: 'text-pink-600', completed: 'text-green-600', drop_offs: 'text-red-600',
};
const SOURCE_LABEL = { own: 'Walk-in / own', manual: 'Walk-in / own', platform: 'Ads', referral: 'Referral', ghl_webhook: 'GHL / website' };

const StudentRow = ({ card, stage, canEdit, onOpen, onMove }) => {
  const pct = card.attendance;
  const atRisk = pct != null && pct < 80 && (card.classes_counted || 0) >= 3;
  const regDate = card.registration_date
    ? new Date(`${card.registration_date}T00:00:00`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })
    : '';
  return (
    <Card className={`border-0 shadow-sm hover:shadow-md transition-shadow ${atRisk ? 'ring-1 ring-red-200' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <button type="button" className="flex-1 min-w-0 text-left" onClick={() => onOpen(card.lead_id)}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-900 hover:text-pink-600">{card.name}</span>
              {atRisk && <Badge className="text-xs bg-red-100 text-red-600">Low attendance</Badge>}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {card.course && (
                <Badge className="text-xs bg-pink-50 text-pink-600">
                  <GraduationCap className="w-3 h-3 mr-1" />{card.course}
                </Badge>
              )}
              {regDate && (
                <span className="text-xs text-slate-500 flex items-center gap-1">
                  <CalendarCheck className="w-3 h-3" />{regDate}{card.registration_time ? ` at ${card.registration_time}` : ''}
                </span>
              )}
              {card.reason && <Badge className="text-xs bg-red-50 text-red-600">{card.reason}</Badge>}
              {card.deposit && <Badge className="text-xs bg-green-50 text-green-700">Deposit{card.deposit !== true ? ` £${Number(card.deposit).toLocaleString('en-GB')}` : ''}</Badge>}
              {card.source && <span className="text-xs text-slate-400">{SOURCE_LABEL[card.source] || card.source}</span>}
            </div>
            {(stage === 'enrolled' || stage === 'completed') && pct != null && (
              <div className="flex items-center gap-2 mt-2 max-w-xs">
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                <span className="text-xs font-semibold text-slate-600">{pct}%</span>
                <span className="text-xs text-slate-400">{card.classes_attended || 0}/{card.classes_counted || 0}</span>
              </div>
            )}
          </button>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <span className={`text-sm font-semibold ${card.value_type === 'actual' ? 'text-green-700' : 'text-slate-500'}`}>{stage === 'drop_offs' || !card.value ? '' : formatGBP(card.value)}</span>
            <div className="flex items-center gap-1.5">
              {canEdit && <MoveToSelect card={card} from={stage} onMove={onMove} />}
              {card.phone && (
                <a href={`tel:${card.phone}`} aria-label={`Call ${card.name}`}
                  className="w-9 h-9 rounded-xl bg-pink-100 text-pink-600 flex items-center justify-center hover:bg-pink-200">
                  <Phone className="w-4 h-4" />
                </a>
              )}
              <button type="button" aria-label={`Open ${card.name}`} onClick={() => onOpen(card.lead_id)}
                className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentsPage;
